/**
 * Cast grading. A cast is judged on two independent axes — how close the spoken
 * chant was, and how close the drawn gesture was — then folded into one grade.
 *
 * Design rule from the brief: being a bit off must never produce a hard failure.
 * It produces a weaker cast. Only genuine non-recognition fails.
 */
import { clamp, type CastGrade } from '@spell/shared-types';

export const GRADE_THRESHOLDS = {
  Perfect: 0.87,
  Normal: 0.66,
  Weak: 0.42,
} as const;

/** Potency multiplier applied to every number the spell produces. */
export const GRADE_POTENCY: Record<CastGrade, number> = {
  Perfect: 1.25,
  Normal: 1.0,
  Weak: 0.62,
  Fail: 0,
};

/** How much of the final score each axis contributes. */
export const GESTURE_WEIGHT = 0.58;
export const CHANT_WEIGHT = 0.42;

export interface CastScore {
  gesture: number;
  chant: number;
  combined: number;
  grade: CastGrade;
  potency: number;
}

export function combineScores(gesture: number, chant: number): CastScore {
  const g = clamp(gesture, 0, 1);
  const c = clamp(chant, 0, 1);
  const combined = clamp(g * GESTURE_WEIGHT + c * CHANT_WEIGHT, 0, 1);
  const grade = gradeFor(combined);
  return { gesture: r(g), chant: r(c), combined: r(combined), grade, potency: GRADE_POTENCY[grade] };
}

export function gradeFor(combined: number): CastGrade {
  if (combined >= GRADE_THRESHOLDS.Perfect) return 'Perfect';
  if (combined >= GRADE_THRESHOLDS.Normal) return 'Normal';
  if (combined >= GRADE_THRESHOLDS.Weak) return 'Weak';
  return 'Fail';
}

/* ------------------------------------------------------------------ */
/* Chant similarity                                                    */
/* ------------------------------------------------------------------ */

/** Damerau-free Levenshtein, iterative, allocation-light. */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  let prev = new Array<number>(b.length + 1);
  let curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    const swap = prev;
    prev = curr;
    curr = swap;
  }
  return prev[b.length];
}

/** Similarity at which a single word counts as heard. */
export const CHANT_TOKEN_THRESHOLD = 0.55;

export interface ChantTokenState {
  /** One word of the registered chant. */
  word: string;
  /** Best similarity found for it anywhere still available in the transcript. */
  score: number;
  /** True once `score` clears `CHANT_TOKEN_THRESHOLD`. */
  heard: boolean;
}

/**
 * Word-by-word recognition state for a chant in progress.
 *
 * This is the per-word half of `chantSimilarity`, pulled out so the HUD can
 * light each word of the incantation as it lands. It has to be the *same*
 * matcher, not a lookalike: a display that credits a word the scorer rejects
 * teaches the player the wrong pronunciation, which is worse than showing them
 * nothing at all.
 *
 * Order-preserving and greedy — a word is only matched against transcript words
 * that come after the previously matched one, so a chant that repeats a word
 * still needs it said twice.
 */
export function chantTokenProgress(
  spokenNormalized: string,
  targetNormalized: string,
): ChantTokenState[] {
  const targetWords = targetNormalized.trim().split(' ').filter(Boolean);
  const spokenWords = spokenNormalized.trim().split(' ').filter(Boolean);

  const out: ChantTokenState[] = [];
  let cursor = 0;
  for (const tw of targetWords) {
    let bestScore = 0;
    let bestIdx = -1;
    for (let i = cursor; i < spokenWords.length; i++) {
      const sw = spokenWords[i];
      const s = 1 - levenshtein(sw, tw) / Math.max(sw.length, tw.length);
      if (s > bestScore) {
        bestScore = s;
        bestIdx = i;
      }
    }
    const heard = bestIdx >= 0 && bestScore >= CHANT_TOKEN_THRESHOLD;
    if (heard) cursor = bestIdx + 1;
    out.push({ word: tw, score: clamp(bestScore, 0, 1), heard });
  }
  return out;
}

/**
 * Compare a recognised transcript against the registered chant.
 *
 * Speech recognition is noisy, so we score three ways and take the best:
 *  - whole-string edit distance,
 *  - best matching window inside a longer transcript (recognisers love to add
 *    extra words),
 *  - per-word matching, which rescues "MIRROR BRAKE" vs "MIRROR BREAK".
 */
export function chantSimilarity(spokenNormalized: string, targetNormalized: string): number {
  const spoken = spokenNormalized.trim();
  const target = targetNormalized.trim();
  if (!target) return 0;
  if (!spoken) return 0;
  if (spoken === target) return 1;

  const whole = 1 - levenshtein(spoken, target) / Math.max(spoken.length, target.length);

  const spokenWords = spoken.split(' ').filter(Boolean);
  const targetWords = target.split(' ').filter(Boolean);

  // Sliding window over the transcript, same length as the target phrase.
  let windowBest = 0;
  if (spokenWords.length >= targetWords.length) {
    for (let i = 0; i + targetWords.length <= spokenWords.length; i++) {
      const slice = spokenWords.slice(i, i + targetWords.length).join(' ');
      const s = 1 - levenshtein(slice, target) / Math.max(slice.length, target.length);
      if (s > windowBest) windowBest = s;
    }
  }

  // Greedy per-word match, tolerant of order-preserving insertions. Shared with
  // the HUD's per-word readout so the two can never disagree about a word.
  const tokens = chantTokenProgress(spoken, target);
  const matched = tokens.reduce((sum, t) => sum + (t.heard ? t.score : 0), 0);
  const wordScore = targetWords.length ? matched / targetWords.length : 0;

  // Extra words in the transcript should not be free, but should not be fatal.
  const lengthPenalty =
    spokenWords.length > targetWords.length
      ? clamp(1 - (spokenWords.length - targetWords.length) * 0.06, 0.7, 1)
      : 1;

  return clamp(Math.max(whole, windowBest, wordScore) * lengthPenalty, 0, 1);
}

const r = (v: number) => Math.round(v * 1000) / 1000;
